import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { BrandingInterfaceValue } from "./branding-interface-utils"
import type { BrandingCssVariables, BrandingLink, VoiceStageSetting } from "@/api/admin/branding"

import { Palette } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AdminSettingsLoadState } from "@/features/admin-dashboard/components/admin-settings-load-state"
import { CollapsibleManagementSection } from "@/features/admin-dashboard/components/collapsible-management-section"
import { DelegationModeControl } from "@/features/admin-dashboard/components/delegation-mode-control"
import {
  useBrandingAssetsQuery,
  useBrandingSettingsQuery,
  useDeleteBrandingAssetMutation,
  useUpdateBrandingSettingsMutation,
  useUploadBrandingAssetMutation,
} from "@/features/admin-dashboard/hooks/admin-queries"

import { BrandingAssetGrid } from "./branding-asset-grid"
import { HIGHLIGHT_CSS_VARS } from "./branding-config-constants"
import {
  cloneCss,
  cssVarNames,
  hexToHslTriplet,
  isHslTriplet,
  omitBlankCssValues,
  pickerValueForCss,
  resolvedAssistantName,
} from "./branding-config-utils"
import { BrandingInterfaceSection } from "./branding-interface-section"
import { EMPTY_INTERFACE, interfaceFromSettings, sameInterface } from "./branding-interface-utils"
import { VoiceStageBackgroundSection } from "./voice-stage-background-section"
import {
  normalizeVoiceStage,
  planVoiceStageSave,
  sameVoiceStage,
  VOICE_STAGE_ASSET_KEY,
} from "./voice-stage-background-utils"

export function BrandingConfigPanel() {
  const { t } = useTranslation()
  const { data, isLoading, isFetching, refetch } = useBrandingSettingsQuery()
  const {
    data: assetsData,
    isLoading: assetsLoading,
    isFetching: assetsFetching,
    refetch: refetchAssets,
  } = useBrandingAssetsQuery()
  const update = useUpdateBrandingSettingsMutation()
  const upload = useUploadBrandingAssetMutation()
  const removeAsset = useDeleteBrandingAssetMutation()

  const [appName, setAppName] = useState("")
  const [assistantName, setAssistantName] = useState("")
  const [organizationName, setOrganizationName] = useState("")
  const [themeColor, setThemeColor] = useState("")
  const [manifestName, setManifestName] = useState("")
  const [manifestShortName, setManifestShortName] = useState("")
  const [cssVariables, setCssVariables] = useState<BrandingCssVariables>(() => cloneCss())
  const [links, setLinks] = useState<BrandingLink[]>([])
  const [voiceStage, setVoiceStage] = useState<VoiceStageSetting>({ mode: "sky" })
  const [interfaceValue, setInterfaceValue] = useState<BrandingInterfaceValue>(EMPTY_INTERFACE)
  const [showAllTokens, setShowAllTokens] = useState(false)

  useEffect(() => {
    if (!data) {
      return
    }
    setAppName(data.app_name ?? "")
    setAssistantName(data.assistant_name ?? "")
    setOrganizationName(data.organization_name ?? "")
    setThemeColor(data.theme_color ?? "")
    setManifestName(data.manifest_name ?? "")
    setManifestShortName(data.manifest_short_name ?? "")
    setCssVariables(cloneCss(data.css_variables))
    setLinks(Array.isArray(data.links) ? data.links.map((link) => ({ ...link })) : [])
    setVoiceStage(normalizeVoiceStage(data.voice_stage))
    setInterfaceValue(interfaceFromSettings(data))
  }, [data])

  const hasUnsavedChanges = useMemo(() => {
    if (!data) {
      return false
    }
    return (
      (data.app_name ?? "") !== appName.trim() ||
      (data.assistant_name ?? "") !== assistantName.trim() ||
      (data.organization_name ?? "") !== organizationName.trim() ||
      (data.theme_color ?? "") !== themeColor.trim() ||
      (data.manifest_name ?? "") !== manifestName.trim() ||
      (data.manifest_short_name ?? "") !== manifestShortName.trim() ||
      JSON.stringify(cloneCss(data.css_variables)) !== JSON.stringify(cssVariables) ||
      JSON.stringify(data.links) !== JSON.stringify(links) ||
      !sameVoiceStage(data.voice_stage, voiceStage) ||
      !sameInterface(interfaceFromSettings(data), interfaceValue)
    )
  }, [
    data,
    appName,
    assistantName,
    organizationName,
    themeColor,
    manifestName,
    manifestShortName,
    cssVariables,
    links,
    voiceStage,
    interfaceValue,
  ])

  const setCssValue = (theme: "light" | "dark", name: string, value: string) => {
    setCssVariables((current) => ({
      ...current,
      [theme]: { ...(current[theme] ?? {}), [name]: value },
    }))
  }

  const voiceStageImage = assetsData?.assets[VOICE_STAGE_ASSET_KEY] ?? null

  const handleSave = async () => {
    const voiceStageSave = planVoiceStageSave({
      stored: data?.voice_stage,
      value: voiceStage,
      image: voiceStageImage,
      t,
    })
    if ("error" in voiceStageSave) {
      toast.error(voiceStageSave.error)
      return
    }
    try {
      await update.mutateAsync({
        app_name: appName.trim() || null,
        assistant_name: assistantName.trim() || null,
        organization_name: organizationName.trim() || null,
        theme_color: themeColor.trim() || null,
        manifest_name: manifestName.trim() || null,
        manifest_short_name: manifestShortName.trim() || null,
        css_variables: omitBlankCssValues(cssVariables),
        links,
        ...interfaceValue,
        ...voiceStageSave.patch,
      })
      toast.success(t("adminDashboard.branding.saved", { defaultValue: "Branding saved" }))
    } catch (error) {
      toast.error(
        t("adminDashboard.branding.saveFailed", { defaultValue: "Could not save branding" }),
        {
          description: error instanceof Error ? error.message : undefined,
        }
      )
    }
  }

  const handleUpload = async (assetKey: string, file: File | undefined) => {
    if (!file) {
      return
    }
    try {
      await upload.mutateAsync({ assetKey, file })
      toast.success(t("adminDashboard.branding.assetUploaded", { defaultValue: "Asset uploaded" }))
    } catch (error) {
      toast.error(
        t("adminDashboard.branding.assetUploadFailed", { defaultValue: "Could not upload asset" }),
        { description: error instanceof Error ? error.message : undefined }
      )
    }
  }

  const handleDeleteAsset = async (assetKey: string) => {
    try {
      await removeAsset.mutateAsync(assetKey)
      toast.success(t("adminDashboard.branding.assetDeleted", { defaultValue: "Asset removed" }))
    } catch (error) {
      toast.error(
        t("adminDashboard.branding.assetDeleteFailed", { defaultValue: "Could not remove asset" }),
        { description: error instanceof Error ? error.message : undefined }
      )
    }
  }

  const addLink = () => {
    setLinks((current) => [
      ...current,
      {
        id: `link-${crypto.randomUUID().slice(0, 8)}`,
        label: "",
        // Keep the URL empty so pasting cannot duplicate the https:// placeholder.
        url: "",
        placement: "chat_footer",
      },
    ])
  }

  const tokenNames = showAllTokens ? cssVarNames(cssVariables) : [...HIGHLIGHT_CSS_VARS]
  return (
    <CollapsibleManagementSection
      icon={<Palette className="size-5" />}
      id="branding"
      isDirty={hasUnsavedChanges}
      storageKey="admin-management-branding-expanded"
      title={t("adminDashboard.branding.title", { defaultValue: "Branding" })}
      headerActions={
        <div className="flex items-center gap-2">
          <DelegationModeControl capability="branding" />
          <Button
            disabled={!hasUnsavedChanges || update.isPending}
            size="sm"
            onClick={() => void handleSave()}
          >
            {t("common.save", { defaultValue: "Save" })}
          </Button>
        </div>
      }
    >
      {isLoading || assetsLoading || !data || !assetsData ? (
        <AdminSettingsLoadState
          loading={isLoading || assetsLoading}
          retrying={isFetching || assetsFetching}
          onRetry={() => {
            if (!data) void refetch()
            if (!assetsData) void refetchAssets()
          }}
        />
      ) : (
        <div className="grid gap-8">
          <p className="text-muted-foreground text-sm">
            {t("adminDashboard.branding.help", {
              defaultValue:
                "Names, colors, logos and footer links for this environment. Saved values live in Cosmos and apply on the next page load — no deploy.",
            })}
          </p>

          <section className="grid gap-3">
            <h3 className="text-sm font-semibold">
              {t("adminDashboard.branding.identity", { defaultValue: "Identity" })}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="branding-app-name">
                  {t("adminDashboard.branding.appName", { defaultValue: "App name" })}
                </Label>
                <Input
                  id="branding-app-name"
                  value={appName}
                  onChange={(event) => setAppName(event.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="branding-assistant-name">
                  {t("adminDashboard.branding.assistantName", { defaultValue: "Assistant name" })}
                </Label>
                <Input
                  id="branding-assistant-name"
                  placeholder={resolvedAssistantName(assistantName, appName)}
                  value={assistantName}
                  onChange={(event) => setAssistantName(event.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.branding.assistantNameHelp", {
                    defaultValue: "Leave blank to use the app name. Prompts resolve to: {{name}}",
                    name: resolvedAssistantName(assistantName, appName),
                  })}
                </p>
              </div>
              <div className="grid gap-1.5 sm:col-span-2">
                <Label htmlFor="branding-org-name">
                  {t("adminDashboard.branding.organizationName", {
                    defaultValue: "Organization name",
                  })}
                </Label>
                <Input
                  id="branding-org-name"
                  value={organizationName}
                  onChange={(event) => setOrganizationName(event.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="grid gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">
                {t("adminDashboard.branding.colors", { defaultValue: "Colors" })}
              </h3>
              <Button
                size="sm"
                type="button"
                variant="ghost"
                onClick={() => setShowAllTokens((open) => !open)}
              >
                {showAllTokens
                  ? t("adminDashboard.branding.showFewerTokens", {
                      defaultValue: "Show primary tokens",
                    })
                  : t("adminDashboard.branding.showAllTokens", { defaultValue: "Show all tokens" })}
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.branding.colorsHelp", {
                defaultValue:
                  "Values are stored as HSL triplets (e.g. 19 100% 50%). The picker writes that form — never hex — because Tailwind consumes hsl(var(--token)).",
              })}
            </p>
            {(["light", "dark"] as const).map((theme) => (
              <div key={theme} className="grid gap-2">
                <p className="text-xs font-medium tracking-wide uppercase">{theme}</p>
                <div className="grid gap-2">
                  {tokenNames.map((name) => {
                    const current = cssVariables[theme]?.[name] ?? ""
                    const isColor =
                      !name.includes("radius") && (current === "" || isHslTriplet(current))
                    return (
                      <div
                        key={`${theme}-${name}`}
                        className="flex min-w-0 flex-wrap items-center gap-2"
                      >
                        <Label
                          className="w-48 shrink-0 font-mono text-xs"
                          htmlFor={`css-${theme}-${name}`}
                        >
                          {name}
                        </Label>
                        {isColor ? (
                          <input
                            aria-label={`${theme} ${name}`}
                            className="border-input size-9 shrink-0 cursor-pointer rounded border"
                            type="color"
                            value={pickerValueForCss(current)}
                            onChange={(event) => {
                              const triplet = hexToHslTriplet(event.target.value)
                              if (triplet) {
                                setCssValue(theme, name, triplet)
                              }
                            }}
                          />
                        ) : null}
                        <Input
                          className="min-w-40 flex-1 font-mono text-xs"
                          id={`css-${theme}-${name}`}
                          value={current}
                          onChange={(event) => setCssValue(theme, name, event.target.value)}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </section>

          <BrandingAssetGrid
            assets={assetsData.assets}
            deleting={removeAsset.isPending}
            onDelete={(assetKey) => void handleDeleteAsset(assetKey)}
            onUpload={(assetKey, file) => void handleUpload(assetKey, file)}
          />

          <VoiceStageBackgroundSection
            busy={upload.isPending || removeAsset.isPending}
            image={voiceStageImage}
            suggestedColor={pickerValueForCss(cssVariables.light?.["--primary"] || "221 83% 53%")}
            value={voiceStage}
            onChange={setVoiceStage}
            onRemoveImage={() => void handleDeleteAsset(VOICE_STAGE_ASSET_KEY)}
            onUploadImage={(file) => void handleUpload(VOICE_STAGE_ASSET_KEY, file)}
          />

          <BrandingInterfaceSection value={interfaceValue} onChange={setInterfaceValue} />

          <section className="grid gap-3">
            <h3 className="text-sm font-semibold">
              {t("adminDashboard.branding.pwa", { defaultValue: "PWA / home screen" })}
            </h3>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="grid gap-1.5">
                <Label htmlFor="branding-manifest-name">
                  {t("adminDashboard.branding.manifestName", { defaultValue: "Manifest name" })}
                </Label>
                <Input
                  id="branding-manifest-name"
                  value={manifestName}
                  onChange={(event) => setManifestName(event.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="branding-manifest-short">
                  {t("adminDashboard.branding.manifestShortName", { defaultValue: "Short name" })}
                </Label>
                <Input
                  id="branding-manifest-short"
                  value={manifestShortName}
                  onChange={(event) => setManifestShortName(event.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="branding-theme-color">
                  {t("adminDashboard.branding.themeColor", { defaultValue: "Theme color (hex)" })}
                </Label>
                <Input
                  id="branding-theme-color"
                  placeholder="#ffffff"
                  value={themeColor}
                  onChange={(event) => setThemeColor(event.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="grid gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">
                {t("adminDashboard.branding.links", { defaultValue: "Chat footer links" })}
              </h3>
              <Button size="sm" type="button" variant="outline" onClick={addLink}>
                {t("adminDashboard.branding.addLink", { defaultValue: "Add link" })}
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.branding.linksHelp", {
                defaultValue:
                  "Shown next to the AI-generated-content warning. Placement is chat footer only.",
              })}
            </p>
            {links.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("adminDashboard.branding.noLinks", { defaultValue: "No footer links." })}
              </p>
            ) : (
              <div className="grid gap-3">
                {links.map((link, index) => (
                  <div
                    key={link.id}
                    className="grid gap-2 rounded-md border p-3 sm:grid-cols-[1fr_1fr_2fr_auto]"
                  >
                    <Input
                      aria-label={t("adminDashboard.branding.linkId", { defaultValue: "Id" })}
                      placeholder="id"
                      value={link.id}
                      onChange={(event) => {
                        const value = event.target.value
                        setLinks((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, id: value } : item
                          )
                        )
                      }}
                    />
                    <Input
                      aria-label={t("adminDashboard.branding.linkLabel", { defaultValue: "Label" })}
                      value={link.label}
                      placeholder={t("adminDashboard.branding.linkLabel", {
                        defaultValue: "Label",
                      })}
                      onChange={(event) => {
                        const value = event.target.value
                        setLinks((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, label: value } : item
                          )
                        )
                      }}
                    />
                    <Input
                      aria-label={t("adminDashboard.branding.linkUrl", { defaultValue: "URL" })}
                      placeholder="https://"
                      value={link.url}
                      onChange={(event) => {
                        const value = event.target.value
                        setLinks((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, url: value } : item
                          )
                        )
                      }}
                    />
                    <Button
                      size="sm"
                      type="button"
                      variant="ghost"
                      onClick={() =>
                        setLinks((current) => current.filter((_, itemIndex) => itemIndex !== index))
                      }
                    >
                      {t("adminDashboard.branding.removeLink", { defaultValue: "Remove" })}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </CollapsibleManagementSection>
  )
}
